import { LoadingButton } from '@one-thd/sui-atomic-components';
import { Printer } from '@one-thd/sui-icons';
import React, { useState } from 'react';
import { useThdCustomer } from '@thd-olt-functional/customer-information';

import { ExportPurchaseHistory } from '../../../api/oms/order-history';
import { ERR_GENERIC } from '../../../constants/response-error-messages';
import {
  IExportPurchaseHistoryRequest,
  IPrintButton,
} from '../../../models/purchase-history';
import {
  IError,
  pushPurchaseHistoryHeaderButtonEvent,
  setPrintHistoryError,
} from '../../../utils/purchase-history/analytics';
import { useCommonContext } from '../../../hooks/useContexts';

const generateDefaultFileName = () => {
  const dateTime = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(new Date());

  const [date, time] = dateTime ? dateTime.split('at') : ['', ''];
  const formattedDate = date?.slice(0, -1)?.replace(/[\s,]+/g, '-');
  const formattedTime = time?.slice(1)?.replace(' ', '-').replace(':', '-');
  return `Purchase_History_${formattedDate}_${formattedTime}.pdf`;
};

export const PrintButton = ({
  orderHistory: {
    customerAccountId = '',
    endDate,
    pageNumber,
    pageSize,
    resultsFilter = '',
    searchBy,
    searchType = 'ORDERS',
    sortBy = '',
    sortOrder = 'asc',
    startDate,
    searchValue,
    filters,
  },
  setAlertState,
}: IPrintButton) => {
  const { userID } = useThdCustomer() ?? {};
  const [loading, setLoading] = useState(false);
  const { timezone } = useCommonContext();

  const closeAlert = () => {
    if (setAlertState) setAlertState(null);
  };

  const printClick = async () => {
    try {
      setLoading(true);
      const request: IExportPurchaseHistoryRequest = {
        orderHistoryRequest: {
          customerAccountId,
          resultsFilter,
          searchBy,
          searchType,
          sortBy,
          sortOrder,
          pageSize,
          pageNumber,
          startDate,
          endDate,
          searchValue,
          filters,
          exportType: 'pdf',
          timezone,
        },
      };
      pushPurchaseHistoryHeaderButtonEvent('print history');
      if (request.orderHistoryRequest.customerAccountId) {
        const response = await ExportPurchaseHistory(request, userID);

        if (response.data) {
          const blob = new Blob([response.data], {
            type: 'data:application/pdf,',
          });

          const fileName = generateDefaultFileName();

          const anchor = document.createElement('a');
          document.body.appendChild(anchor);
          anchor.setAttribute('style', 'display: none');
          const url = (window.URL || window.webkitURL).createObjectURL(blob);
          anchor.href = url;
          anchor.download = fileName;
          anchor.click();

          setTimeout(() => {
            anchor.remove();
          }, 500);

          setAlertState({
            alertMessage:
              'Purchase History Data successfully exported. Please check your browser downloads folder for the file.',
            status: 'success',
            onClose: closeAlert,
          });
          return;
        }
        setAlertState({
          alertMessage: ERR_GENERIC.message,
          status: 'error',
        });
      }
    } catch (error: unknown) {
      setAlertState({
        alertMessage: ERR_GENERIC.message,
        status: 'error',
      });
      setPrintHistoryError(error as IError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingButton
        data-testid="print-button"
        loading={loading}
        variant="ghost"
        startIcon={Printer}
        onClick={printClick}
      >
        Print
      </LoadingButton>
    </>
  );
};
